import React, { useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Navigate, useNavigate } from "react-router-dom";

import { VERIFY_COMPANY_TOKEN } from "utility/graphQl/query";
import { TRIGGER_LEARNER_VERIFICATION_EMAIL } from "utility/graphQl/mutation";
import useQueryParams from "components/commons/hooks/useQueryParams";
import { useTranslation } from "lib/i18n/use-translation";
import appInfo from "utility/app-info";

import {
  AuthLayout,
  Heading,
  PrimaryButton,
  TextField,
} from "apps/user/common/components/auth-layout";

import { PageLoading } from "apps/user/learner/components/page-container";

import { logError } from "utility/Function";

import styles from "../sign-up.module.scss";
import containerStyles from "./sign-up-via-company-magic-link.module.scss";

const SignUpViaCompanyMagicLink: React.FC = () => {
  const params = useQueryParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { t } = useTranslation(["auth"]);
  const emailRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [subTitle, setSubTitle] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState("");

  const [
    verifyCompanyToken,
    { loading: verifyTokenLoading, data: verifyTokenData },
  ] = useLazyQuery(VERIFY_COMPANY_TOKEN, {
    fetchPolicy: "no-cache",
    onError(error) {
      logError("Error while fetching create company page", error);
      navigate("/signup/company/invalid-link");
    },
    onCompleted(res) {
      if (res.verifyCompanyToken.pageState === "invalid-token") {
        navigate("/signup/company/invalid-link");
      } else {
        setTitle(res.verifyCompanyToken.title);
        setSubTitle(res.verifyCompanyToken.subTitle);
        setResendEmail("");
      }
    },
  });

  const [learnerVerificationEmail, { loading, error }] = useMutation(
    TRIGGER_LEARNER_VERIFICATION_EMAIL,
    { fetchPolicy: "no-cache" }
  );

  useEffect(() => {
    verifyCompanyToken({
      variables: { companyToken: token, appInfo },
    });
  }, [token, verifyCompanyToken]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const email = resendEmail || emailRef.current?.value || "";
    learnerVerificationEmail({
      variables: { appInfo, companyToken: token, email },
    })
      .then((res) => {
        const { pageState, email: nextEmail, title: nextTitle, subTitle: nextSub } =
          res.data.triggerLearnerVerificationEmail.response;
        if (pageState === "invalid-token") {
          navigate("/signup/company/invalid-link");
          return;
        }
        if (pageState === "email-sent") {
          if (resendEmail) alert(t("auth:magicLink.confirmationSent"));
          setResendEmail(nextEmail);
          setTitle(nextTitle);
          setSubTitle(nextSub);
          return;
        }
        if (pageState === "invalid-email" || pageState === "valid-token") {
          setResendEmail("");
          setTitle(nextTitle);
          setSubTitle(nextSub);
        }
      })
      .catch((err) => logError("Error signing up with one time link", err));
  };

  const handleGoBack = (): void => {
    verifyCompanyToken({ variables: { companyToken: token, appInfo } });
  };

  // Deep-link defence: send tokenless visitors to the branded intro
  // rather than the bare sign-in screen.
  if (!token) return <Navigate to="/get-started" replace />;

  if (verifyTokenLoading) {
    return (
      <div className={containerStyles["page-loading"]}>
        <PageLoading />
      </div>
    );
  }

  if (!verifyTokenData) return null;

  return (
    <AuthLayout variant="form">
      <form
        onSubmit={handleSubmit}
        className={styles["form"]}
        autoComplete="email"
      >
        {title && (
          <Heading>
            <span dangerouslySetInnerHTML={{ __html: title }} />
          </Heading>
        )}
        {subTitle && (
          <p
            className={containerStyles["email"]}
            dangerouslySetInnerHTML={{ __html: subTitle }}
          />
        )}

        {!resendEmail && (
          <TextField
            ref={emailRef}
            label={t("auth:emailEntry.fieldLabel")}
            type="email"
            name="email"
            autoComplete="email"
            placeholder={t("auth:emailEntry.placeholder")}
            required
            error={error?.message}
          />
        )}

        <div className={styles["submit"]}>
          <PrimaryButton type="submit" loading={loading}>
            {resendEmail
              ? t("auth:magicLink.resendEmail")
              : t("auth:magicLink.submit")}
          </PrimaryButton>
          {resendEmail && (
            <PrimaryButton
              variant="secondary"
              type="button"
              onClick={handleGoBack}
            >
              {t("auth:layout.goBack")}
            </PrimaryButton>
          )}
        </div>
      </form>
    </AuthLayout>
  );
};

export default SignUpViaCompanyMagicLink;
